import { supabaseDataProvider } from "ra-supabase-core";
import {
  withLifecycleCallbacks,
  type DataProvider,
  type GetListParams,
  type Identifier,
  type ResourceCallbacks,
} from "ra-core";
import type {
  ContactNote,
  Deal,
  DealNote,
  RAFile,
  Sale,
  SalesFormData,
  SignUpData,
} from "../../types";
import type { ConfigurationContextValue } from "../../root/ConfigurationContext";
import {
  ATTACHMENTS_BUCKET,
  BRANDING_BUCKET,
  getAttachmentPath,
} from "../commons/attachments";
import { getIsInitialized } from "./authProvider";
import { getSupabaseClient } from "./supabase";
import { toPostgrest } from "../../raftel/directory/directoryFilters";
import { withFriendlyPermissionErrors } from "../../raftel/permissionError";

const getBaseDataProvider = () =>
  supabaseDataProvider({
    instanceUrl: import.meta.env.VITE_SUPABASE_URL,
    apiKey: import.meta.env.VITE_SB_PUBLISHABLE_KEY,
    supabaseClient: getSupabaseClient(),
    sortOrder: "asc,desc.nullslast" as any,
  });

const processCompanyLogo = async (params: any) => {
  const logo = params.data.logo;

  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo, BRANDING_BUCKET);
  }

  return {
    ...params,
    data: {
      ...params.data,
      logo,
    },
  };
};

const getDataProviderWithCustomMethods = () => {
  const baseDataProvider = getBaseDataProvider();

  return {
    ...baseDataProvider,
    async getList(resource: string, params: GetListParams) {
      if (resource === "companies") {
        return baseDataProvider.getList("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getList("contacts_summary", params);
      }
      if (
        resource === "directory" &&
        params.sort?.field &&
        params.sort.field !== "id"
      ) {
        // Raftel: desempate por id para que las páginas no repitan ni salten filas
        const dir = params.sort.order === "ASC" ? "asc" : "desc.nullslast";
        return baseDataProvider.getList(resource, {
          ...params,
          // filtros de la pantalla (zona, radio, No contactar…) → PostgREST
          filter: await toPostgrest(params.filter ?? {}),
          sort: { ...params.sort, field: `${params.sort.field}.${dir},id` },
        });
      }
      if (resource === "activity_log") {
        const { data, total } = await baseDataProvider.getList(
          "activity_log",
          params,
        );
        // Rename snake_case view columns to camelCase to match Activity type
        return {
          data: data.map((row: any) => ({
            ...row,
            contactNote: row.contact_note ?? undefined,
            dealNote: row.deal_note ?? undefined,
            contact_note: undefined,
            deal_note: undefined,
          })),
          total,
        };
      }

      return baseDataProvider.getList(resource, params);
    },
    async getOne(resource: string, params: any) {
      if (resource === "companies") {
        return baseDataProvider.getOne("companies_summary", params);
      }
      if (resource === "contacts") {
        return baseDataProvider.getOne("contacts_summary", params);
      }

      return baseDataProvider.getOne(resource, params);
    },

    async signUp({ email, password, first_name, last_name }: SignUpData) {
      const response = await getSupabaseClient().auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name,
            last_name,
          },
        },
      });

      if (!response.data?.user || response.error) {
        console.error("signUp.error", response.error);
        throw new Error(response?.error?.message || "Failed to create account");
      }

      // Update the is initialized cache
      (getIsInitialized as any)._is_initialized_cache = true;

      return {
        id: response.data.user.id,
        email,
        password,
      };
    },
    async salesCreate(body: SalesFormData) {
      const { data, error } = await getSupabaseClient().functions.invoke<{
        data: Sale;
      }>("users", {
        method: "POST",
        body,
      });

      if (!data || error) {
        console.error("salesCreate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw Object.assign(
          new Error(errorDetails?.message || "Failed to create the user"),
          { code: errorDetails?.code, email: errorDetails?.email },
        );
      }

      return data.data;
    },
    async salesUpdate(
      id: Identifier,
      data: Partial<Omit<SalesFormData, "password">>,
    ) {
      const {
        email,
        secondary_emails,
        first_name,
        last_name,
        administrator,
        avatar,
        disabled,
      } = data;

      const { data: updatedData, error } =
        await getSupabaseClient().functions.invoke<{
          data: Sale;
        }>("users", {
          method: "PATCH",
          body: {
            sales_id: id,
            email,
            secondary_emails,
            first_name,
            last_name,
            administrator,
            disabled,
            avatar,
          },
        });

      if (!updatedData || error) {
        console.error("salesUpdate.error", error);
        const errorDetails = await (async () => {
          try {
            return (await error?.context?.json()) ?? {};
          } catch {
            return {};
          }
        })();
        throw Object.assign(
          new Error(
            errorDetails?.message || "Failed to update account manager",
          ),
          { code: errorDetails?.code, email: errorDetails?.email },
        );
      }

      return updatedData.data;
    },
    async updatePassword(id: Identifier) {
      const { data: passwordUpdated, error } =
        await getSupabaseClient().functions.invoke<boolean>("update_password", {
          method: "PATCH",
          body: {
            sales_id: id,
          },
        });

      if (!passwordUpdated || error) {
        console.error("update_password.error", error);
        throw new Error("Failed to update password");
      }

      return passwordUpdated;
    },
    async unarchiveDeal(deal: Deal) {
      // get all deals where stage is the same as the deal to unarchive
      const { data: deals } = await baseDataProvider.getList<Deal>("deals", {
        filter: { stage: deal.stage },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: "index", order: "ASC" },
      });

      // set index for each deal starting from 1, if the deal to unarchive is found, set its index to the last one
      const updatedDeals = deals.map((d, index) => ({
        ...d,
        index: d.id === deal.id ? 0 : index + 1,
        archived_at: d.id === deal.id ? null : d.archived_at,
      }));

      return await Promise.all(
        updatedDeals.map((updatedDeal) =>
          baseDataProvider.update("deals", {
            id: updatedDeal.id,
            data: updatedDeal,
            previousData: deals.find((d) => d.id === updatedDeal.id),
          }),
        ),
      );
    },
    async isInitialized() {
      return getIsInitialized();
    },
    async mergeContacts(sourceId: Identifier, targetId: Identifier) {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "merge_contacts",
        {
          method: "POST",
          body: { loserId: sourceId, winnerId: targetId },
        },
      );

      if (error) {
        console.error("merge_contacts.error", error);
        throw new Error("Failed to merge contacts");
      }

      return data;
    },
    async getConfiguration(): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.getOne("configuration", {
        id: 1,
      });
      return (data?.config as ConfigurationContextValue) ?? {};
    },
    async updateConfiguration(
      config: ConfigurationContextValue,
    ): Promise<ConfigurationContextValue> {
      const { data } = await baseDataProvider.update("configuration", {
        id: 1,
        data: { config },
        previousData: { id: 1 },
      });
      return data.config as ConfigurationContextValue;
    },
  } satisfies DataProvider;
};

export type CrmDataProvider = ReturnType<
  typeof getDataProviderWithCustomMethods
>;

// Block 2A M4: an account admin READS its users' rows but RLS lets it write only its own workspace, so a write on
// someone else's row matches 0 rows without any database error. update/delete ask PostgREST for one object and get
// a 406 (PGRST116); updateMany/deleteMany get back fewer rows than requested. Both become a visible message.
export const ZERO_ROWS_MESSAGE =
  "No se guardó el cambio: solo puedes modificar registros de tu propio espacio.";

const isNoRowError = (error: unknown): boolean => {
  const e = error as { status?: unknown; body?: { code?: unknown } } | null;
  return e?.status === 406 || e?.body?.code === "PGRST116";
};

export const withZeroRowsWarning = <T extends DataProvider>(
  provider: T,
): T => ({
  ...provider,
  update: async (resource, params) => {
    try {
      return await provider.update(resource, params);
    } catch (error) {
      throw isNoRowError(error) ? new Error(ZERO_ROWS_MESSAGE) : error;
    }
  },
  delete: async (resource, params) => {
    try {
      return await provider.delete(resource, params);
    } catch (error) {
      throw isNoRowError(error) ? new Error(ZERO_ROWS_MESSAGE) : error;
    }
  },
  updateMany: async (resource, params) => {
    const result = await provider.updateMany(resource, params);
    if ((result.data?.length ?? 0) < params.ids.length) {
      throw new Error(ZERO_ROWS_MESSAGE);
    }
    return result;
  },
  deleteMany: async (resource, params) => {
    const result = await provider.deleteMany(resource, params);
    if ((result.data?.length ?? 0) < params.ids.length) {
      throw new Error(ZERO_ROWS_MESSAGE);
    }
    return result;
  },
});

const processConfigLogo = async (logo: any): Promise<string> => {
  if (typeof logo === "string") return logo;
  if (logo?.rawFile instanceof File) {
    await uploadToBucket(logo, BRANDING_BUCKET);
    return logo.src;
  }
  return logo?.src ?? "";
};

const lifeCycleCallbacks: ResourceCallbacks[] = [
  {
    resource: "configuration",
    beforeUpdate: async (params) => {
      const config = params.data.config;
      if (config) {
        config.lightModeLogo = await processConfigLogo(config.lightModeLogo);
        config.darkModeLogo = await processConfigLogo(config.darkModeLogo);
      }
      return params;
    },
  },
  {
    resource: "contact_notes",
    beforeSave: async (data: ContactNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map(uploadNoteAttachment),
        );
      }
      return data;
    },
  },
  {
    resource: "deal_notes",
    beforeSave: async (data: DealNote, _, __) => {
      if (data.attachments) {
        data.attachments = await Promise.all(
          data.attachments.map(uploadNoteAttachment),
        );
      }
      return data;
    },
  },
  {
    resource: "sales",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
    beforeSave: async (data: Sale, _, __) => {
      // only a newly picked photo is uploaded; a saved URL (even an old one of attachments) is kept as is
      if (data.avatar?.rawFile instanceof File) {
        await uploadToBucket(data.avatar, BRANDING_BUCKET);
      }
      return data;
    },
  },
  {
    resource: "contacts",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "first_name",
        "last_name",
        "company_name",
        "title",
        "email",
        "phone",
        "background",
      ])(params);
    },
  },
  {
    resource: "companies",
    beforeGetList: async (params) => {
      return applyFullTextSearch([
        "name",
        "phone_number",
        "website",
        "zipcode",
        "city",
        "state_abbr",
      ])(params);
    },
    beforeCreate: async (params) => {
      const createParams = await processCompanyLogo(params);

      return {
        ...createParams,
        data: {
          created_at: new Date().toISOString(),
          ...createParams.data,
        },
      };
    },
    beforeUpdate: async (params) => {
      return await processCompanyLogo(params);
    },
  },
  {
    resource: "contacts_summary",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["first_name", "last_name"])(params);
    },
  },
  {
    resource: "deals",
    beforeGetList: async (params) => {
      return applyFullTextSearch(["name", "category", "description"])(params);
    },
  },
];

export const getDataProvider = () => {
  if (import.meta.env.VITE_SUPABASE_URL === undefined) {
    throw new Error("Please set the VITE_SUPABASE_URL environment variable");
  }
  if (import.meta.env.VITE_SB_PUBLISHABLE_KEY === undefined) {
    throw new Error(
      "Please set the VITE_SB_PUBLISHABLE_KEY environment variable",
    );
  }
  return withFriendlyPermissionErrors(
    withLifecycleCallbacks(
      withZeroRowsWarning(getDataProviderWithCustomMethods()),
      lifeCycleCallbacks,
    ) as CrmDataProvider,
  );
};

const applyFullTextSearch = (columns: string[]) => (params: GetListParams) => {
  if (!params.filter?.q) {
    return params;
  }
  const { q, ...filter } = params.filter;
  return {
    ...params,
    filter: {
      ...filter,
      "@or": columns.reduce((acc, column) => {
        if (column === "email")
          return {
            ...acc,
            [`email_fts@ilike`]: q,
          };
        if (column === "phone")
          return {
            ...acc,
            [`phone_fts@ilike`]: q,
          };
        else
          return {
            ...acc,
            [`${column}@ilike`]: q,
          };
      }, {}),
    },
  };
};

// Note attachments are saved with their storage path only, never a URL: the bucket is private (Block 2A) and the
// screen signs the path when showing it. A file that could not be uploaded (no path) keeps its external src.
const uploadNoteAttachment = async (fi: RAFile): Promise<RAFile> => {
  const uploaded = await uploadToBucket(fi);
  return uploaded.path ? { ...uploaded, src: "" } : uploaded;
};

// Block 2A M2b: company logos, settings logos and seller photos go to the public `branding` bucket (shown by plain
// URL, even on the login page); note and email attachments stay in the private `attachments` bucket.
const uploadToBucket = async (
  fi: RAFile,
  bucket = ATTACHMENTS_BUCKET,
): Promise<RAFile> => {
  if (
    bucket === ATTACHMENTS_BUCKET &&
    !fi.src.startsWith("blob:") &&
    !fi.src.startsWith("data:")
  ) {
    // Sign URL check if path exists in the bucket (old records: path taken from their public URL)
    const existingPath = getAttachmentPath(fi);
    if (existingPath) {
      const { error } = await getSupabaseClient()
        .storage.from(ATTACHMENTS_BUCKET)
        .createSignedUrl(existingPath, 60);

      if (!error) {
        return { ...fi, path: existingPath };
      }
    }
  }

  const dataContent = fi.src
    ? await fetch(fi.src)
        .then((res) => {
          if (res.status !== 200) {
            return null;
          }
          return res.blob();
        })
        .catch(() => null)
    : fi.rawFile;

  if (dataContent == null) {
    // We weren't able to download the file from its src (e.g. user must be signed in on another website to access it)
    // or the file has no content (not probable)
    // In that case, just return it as is: when trying to download it, users should be redirected to the other website
    // and see they need to be signed in. It will then be their responsibility to upload the file back to the note.
    return fi;
  }

  const file = fi.rawFile;
  // the image editor names a re-cropped file after its old URL: keep a plain extension only, never a "/"
  const fileExt = /\.[a-z0-9]{1,8}$/i.exec(file.name)?.[0] ?? "";
  const fileName = `${crypto.randomUUID()}${fileExt}`;
  // E6.2b: files live under "<workspace id>/" (a freelancer may only write its own folder; the owner, anywhere)
  const { data: workspaceId } = await getSupabaseClient().rpc(
    "default_workspace_id",
  );
  const filePath = workspaceId ? `${workspaceId}/${fileName}` : fileName;
  const { error: uploadError } = await getSupabaseClient()
    .storage.from(bucket)
    .upload(filePath, dataContent);

  if (uploadError) {
    console.error("uploadError", uploadError);
    throw new Error(
      "No se pudo subir el archivo. Revisa tu conexión e inténtalo de nuevo; si sigue fallando, avisa al dueño del CRM.",
    );
  }

  const { data } = getSupabaseClient()
    .storage.from(bucket)
    .getPublicUrl(filePath);

  fi.path = filePath;
  fi.src = data.publicUrl;

  // save MIME type
  const mimeType = file.type;
  fi.type = mimeType;

  return fi;
};
